import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useApiClient from "../hooks/useApiClient";
import { publicApiClient } from "../ApiClient";
import type { Session, Appointment, PageSettings, Slot } from "../types";

// ---------------------------------------------------------------------------
// Public hooks (no auth required)
// ---------------------------------------------------------------------------

export const usePublicConsultationQuery = (tenantId: string) => {
  return useQuery<{ sessions: Session[]; pageSettings: PageSettings }, AxiosError>({
    queryFn: async () => (await publicApiClient.get(`/tenant/${tenantId}/consultation`)).data,
    queryKey: ["consultation", tenantId],
  });
};

export const useAvailableSlotsQuery = (tenantId: string, sessionId: string | null) => {
  return useQuery<
    { session: Session; slots: Slot[]; hasAvailableSlots: boolean },
    AxiosError
  >({
    queryFn: async () =>
      (await publicApiClient.get(`/tenant/${tenantId}/consultation/${sessionId}/slots`)).data,
    queryKey: ["consultation", tenantId, "slots", sessionId],
    enabled: !!sessionId,
  });
};

export const useBookAppointmentMutation = (tenantId: string, sessionId: string | null) => {
  return useMutation<
    { id: string; confirmationKey: string },
    AxiosError,
    { name: string; email: string; phone?: string; slotTime: string; consultantNumber: number }
  >({
    mutationFn: async (data) =>
      (await publicApiClient.post(`/tenant/${tenantId}/consultation/${sessionId}/book`, data)).data,
    mutationKey: ["consultation", "book", tenantId, sessionId],
  });
};

export const useNotifyMutation = (tenantId: string, sessionId: string | null) => {
  return useMutation<unknown, AxiosError, { email: string }>({
    mutationFn: async (data) =>
      (await publicApiClient.post(`/tenant/${tenantId}/consultation/${sessionId}/notify`, data)).data,
    mutationKey: ["consultation", "notify", tenantId, sessionId],
  });
};

// ---------------------------------------------------------------------------
// Admin hooks (auth required)
// ---------------------------------------------------------------------------

export const useAllSessionsQuery = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useQuery<Session[], AxiosError>({
    queryFn: async () => (await privateApiClient.get(`/tenant/${tenantId}/consultation/admin/all`)).data,
    queryKey: ["consultation", tenantId, "admin", "all"],
  });
};

export const useSessionAppointmentsQuery = (tenantId: string, sessionId: string | null) => {
  const { privateApiClient } = useApiClient();
  return useQuery<Appointment[], AxiosError>({
    queryFn: async () =>
      (await privateApiClient.get(`/tenant/${tenantId}/consultation/${sessionId}/appointments`)).data,
    queryKey: ["consultation", tenantId, "appointments", sessionId],
    enabled: !!sessionId,
  });
};

export const useCreateSessionMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useMutation<{ id: string }, AxiosError, Partial<Session>>({
    mutationFn: async (data) =>
      (await privateApiClient.post(`/tenant/${tenantId}/consultation`, data)).data,
    mutationKey: ["consultation", "create", tenantId],
  });
};

export const useUpdateSessionMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useMutation<unknown, AxiosError, { sessionId: string; data: Partial<Session> }>({
    mutationFn: async ({ sessionId, data }) =>
      (await privateApiClient.put(`/tenant/${tenantId}/consultation/${sessionId}`, data)).data,
    mutationKey: ["consultation", "update", tenantId],
  });
};

export const useDeleteSessionMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useMutation<unknown, AxiosError, string>({
    mutationFn: async (sessionId) =>
      (await privateApiClient.delete(`/tenant/${tenantId}/consultation/${sessionId}`)).data,
    mutationKey: ["consultation", "delete", tenantId],
  });
};

export const useUpdatePageSettingsMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useMutation<unknown, AxiosError, PageSettings>({
    mutationFn: async (data) =>
      (await privateApiClient.put(`/tenant/${tenantId}/consultation/settings`, data)).data,
    mutationKey: ["consultation", "settings", tenantId],
  });
};

/**
 * Zapisuje nową kolejność sesji (po przeciągnięciu w DnD w panelu admina).
 * Body: { ids: [sessionId1, sessionId2, ...] } — kolejność w tablicy staje się
 * wartością `displayOrder` (0, 1, 2, ...) w bazie i dyktuje sort na publicznej
 * stronie oraz w panelu admina.
 */
export const useReorderSessionsMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useMutation<unknown, AxiosError, string[]>({
    mutationFn: async (ids) =>
      (await privateApiClient.post(`/tenant/${tenantId}/consultation/reorder`, { ids })).data,
    mutationKey: ["consultation", "reorder", tenantId],
  });
};

export const useCleanupMutation = (tenantId: string) => {
  const { privateApiClient } = useApiClient();
  return useMutation<unknown, AxiosError, void>({
    mutationFn: async () =>
      (await privateApiClient.post(`/tenant/${tenantId}/consultation/cleanup`)).data,
    mutationKey: ["consultation", "cleanup", tenantId],
  });
};
